/**
 * Small shared read hooks/helpers for the comments feature — the pins layer,
 * the overlays (popover/composer) and the panel all resolve thread state and
 * anchor geometry from the SAME inputs, so their views can never disagree
 * within a render pass (docs/04 §3: anchors resolve at render time from the
 * reactive state the board renders from).
 */
import type { CommentThreadInfo } from "@kola/shared";
import { useMemo } from "react";
import { useStore } from "zustand";

import type { BoardSession } from "../../core/session.js";
import type { BoardRect } from "../../registries/toolbar-actions/index.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";
import { resolveElementRect } from "../elements/contained.js";

/** All threads, re-derived when any comment changes (panel order: F-55). */
export function useCommentThreads(session: BoardSession): CommentThreadInfo[] {
  const commentsVersion = useStore(session.store, (s) => s.commentsVersion);
  return useMemo(() => {
    void commentsVersion; // re-derive on any thread change
    return session.board.listThreads();
  }, [session, commentsVersion]);
}

/**
 * Element-rect lookup for anchor resolution, with optional live-grab
 * OVERRIDES: while a peer drags an element, its awareness-streamed preview
 * rect (F-46/C9) substitutes for the doc geometry so anchored pins ride the
 * motion instead of teleporting on the commit.
 */
export function elementRectLookup(
  session: BoardSession,
  grabRects?: ReadonlyMap<string, BoardRect>,
  layout?: GridLayoutSnapshot | null,
): (elementId: string) => BoardRect | null {
  return (elementId) => {
    const grabbed = grabRects?.get(elementId);
    if (grabbed !== undefined) return grabbed;
    const el = session.board.getElement(elementId);
    if (el === undefined) return null;
    // A cell-anchored (contained) element's real rect comes from its cell
    // (F-36), so a comment anchored to it tracks the cell — resolve via layout.
    return resolveElementRect(el, layout ?? null);
  };
}

/**
 * Live element-grab preview rects (elementId → rect): every PEER's
 * awareness-streamed drag rect PLUS this client's own in-flight grab
 * (`localGrab`). Peers exclude self by construction, so without the local
 * entry a pin anchored to the element the user is dragging would stay frozen
 * for the dragger and teleport on release — remote clients saw the motion,
 * the one user manipulating it did not (F-52 / docs/04 §3 "follows drags
 * live"). Own grab is added last: the F-46 soft lock keeps peers off an
 * element we are dragging, and our own preview is the freshest.
 */
export function useGrabRects(session: BoardSession): ReadonlyMap<string, BoardRect> {
  const peers = useStore(session.store, (s) => s.peers);
  const localGrab = useStore(session.store, (s) => s.localGrab);
  return useMemo(() => {
    const map = new Map<string, BoardRect>();
    for (const peer of peers) {
      const grab = peer.grabbedElement;
      if (grab?.rect !== undefined) map.set(grab.elementId, grab.rect);
    }
    if (localGrab?.rect !== undefined) map.set(localGrab.elementId, localGrab.rect);
    return map;
  }, [peers, localGrab]);
}
